// mapMarkerOptions.js

const DARK_PIN_COLOR = '#01a13c';
const LIGHT_PIN_COLOR = '#0b45c4';

const MAX_LABEL_LENGTH = 38;
const PIN_WIDTH = 28;
const ICON_HEIGHT = 44;
const BADGE_HEIGHT = 22;
const BADGE_PADDING_X = 10;
const BADGE_MIN_WIDTH = 56;
const BADGE_GAP = 6;
const ESTIMATED_CHAR_WIDTH = 7;

const clampLabel = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';
  if (text.length <= MAX_LABEL_LENGTH) return text;
  return `${text.slice(0, MAX_LABEL_LENGTH - 3)}...`;
};

const escapeXml = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&apos;');

const createPinIcon = ({ pinColor, labelText, labelTextColor, labelBackgroundColor, labelBorderColor }) => {
  const badgeWidth = Math.max(BADGE_MIN_WIDTH, (labelText.length * ESTIMATED_CHAR_WIDTH) + (BADGE_PADDING_X * 2));
  const badgeX = PIN_WIDTH + BADGE_GAP;
  const badgeY = 2;
  const totalWidth = PIN_WIDTH + BADGE_GAP + badgeWidth;
  const safeLabel = escapeXml(labelText);

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${totalWidth}" height="${ICON_HEIGHT}" viewBox="0 0 ${totalWidth} ${ICON_HEIGHT}"><path fill="${pinColor}" stroke="white" stroke-width="1.5" d="M14 1.5C7.65 1.5 2.5 6.65 2.5 13c0 8.85 11.5 24.5 11.5 24.5S25.5 21.85 25.5 13C25.5 6.65 20.35 1.5 14 1.5z"/><circle cx="14" cy="13" r="4.2" fill="white"/><rect x="${badgeX}" y="${badgeY}" rx="11" ry="11" width="${badgeWidth}" height="${BADGE_HEIGHT}" fill="${labelBackgroundColor}" stroke="${labelBorderColor}" stroke-width="1"/><text x="${badgeX + BADGE_PADDING_X}" y="17" fill="${labelTextColor}" font-family="Manrope, system-ui, sans-serif" font-size="12" font-weight="700">${safeLabel}</text></svg>`;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    width: totalWidth,
    height: ICON_HEIGHT,
  };
};

const buildMarkerLabelText = (pin) => {
  const title = String(pin?.title || 'Untitled Node').trim();
  const fieldLabel = String(pin?.fieldLabel || '').trim();
  if (!fieldLabel) return clampLabel(title);
  return clampLabel(`${title} - ${fieldLabel}`);
};

export const buildMapMarkerOptions = ({ pin, map, google, themeMode }) => {
  const markerTitle = pin.markerTitle || `${pin.title}${pin.subtitle ? ` - ${pin.subtitle}` : ''}`;

  const isDark = String(themeMode || '').toUpperCase() === 'DARK';
  const pinColor = isDark ? DARK_PIN_COLOR : LIGHT_PIN_COLOR;
  const labelColor = isDark ? '#f9fafb' : '#111827';
  const labelBackgroundColor = isDark ? '#101418' : '#ffffff';
  const labelBorderColor = isDark ? '#2f3c47' : '#d1d5db';
  const labelText = buildMarkerLabelText(pin);

  if (!google?.maps?.Size || !google?.maps?.Point) {
    return {
      map,
      position: { lat: pin.latitude, lng: pin.longitude },
      title: markerTitle || labelText,
      visible: true,
      clickable: true,
      optimized: true,
      zIndex: 1000,
    };
  }

  const icon = createPinIcon({
    pinColor,
    labelText,
    labelTextColor: labelColor,
    labelBackgroundColor,
    labelBorderColor,
  });

  return {
    map,
    position: { lat: pin.latitude, lng: pin.longitude },
    title: markerTitle || labelText,
    visible: true,
    clickable: true,
    optimized: false,
    zIndex: 1000,
    icon: {
      url: icon.url,
      scaledSize: new google.maps.Size(icon.width, icon.height),
      anchor: new google.maps.Point(14, 38),
    },
  };
};
